import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { TextButton } from '@zillow/constellation-10';
import BillingConstants from '../../constants/BillingConstants';
import { BillingInvoicePage } from '../BillingInvoicePage';
import { numberUtils } from '@zg-rentals/rental-manager-env';
import PropertyNameList from '../../components/PropertyNameList';
import { HR } from '@zg-rentals/zrs-comet';
import { Box } from '@zillow/constellation-10';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { type AdproductsZuoraBillingInvoiceDto, ZuoraInvoiceType } from '@zg-rentals/ts-adproducts-client';
import { EVENT_IDS } from '../../model/ClickstreamEvents';
import { localDateToDate } from '../../utils/dateUtils';

const invoiceRenderer = (invoice: AdproductsZuoraBillingInvoiceDto) =>
  invoice.invoiceType !== ZuoraInvoiceType.NUCLEUS_LISTING ? (
    <Link to={`/rental-manager/billing/invoice/${invoice.invoiceNumber}?prev=paid`}>
      <TextButton
        data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
        data-eventdescription="View billing invoice page event"
      >
        {invoice.invoiceNumber}
      </TextButton>
    </Link>
  ) : (
    <Box css={{ wordBreak: 'break-all' }}>{invoice.invoiceNumber}</Box>
  );

const fullColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    dataKey: 'invoiceLocalDate',
    width: 1,
    header: 'Date',
    align: 'left',
    renderer: (invoice) =>
      invoice.invoiceLocalDate ? format(localDateToDate(invoice.invoiceLocalDate), 'MM/dd/yy') : '-',
  },
  {
    width: 2,
    align: 'left',
    header: 'Invoice No.',
    dataKey: 'invoiceNumber',
    renderer: invoiceRenderer,
    className: 'TableMFH-data-word-break',
  },
  {
    width: 3,
    align: 'left',
    header: 'Properties',
    dataKey: 'propertyNames',
    renderer: (invoice) => <PropertyNameList invoice={invoice} />,
  },
  {
    width: 2,
    align: 'left',
    header: 'Account Number',
    dataKey: 'accountNumber',
  },
  {
    width: 2,
    header: 'Amount',
    dataKey: 'amount',
    renderer: (invoice) => numberUtils.asCurrency(invoice.amount),
    align: 'right',
  },
  {
    width: 1,
    header: 'Status',
    dataKey: 'balance',
    renderer: (invoice) => (invoice.balance === 0 ? 'Paid' : 'Partially Paid'),
    align: 'center',
  },
];

const mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    dataKey: 'invoiceLocalDate',
    width: 2,
    header: 'Date',
    align: 'left',
    renderer: (invoice) => invoice.invoiceLocalDate && format(localDateToDate(invoice.invoiceLocalDate), 'MM/dd/yy'),
  },
  {
    width: 4,
    header: 'Invoice',
    dataKey: 'invoiceNumber',
    align: 'left',
    renderer: (invoice) => (
      <>
        {invoiceRenderer(invoice)}
        {invoice.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING && <HR />}
        <div>
          <PropertyNameList invoice={invoice} />
        </div>
        <div>{invoice.accountNumber}</div>
      </>
    ),
  },
  {
    width: 2,
    header: 'Amount',
    dataKey: 'amount',
    renderer: (invoice) => numberUtils.asCurrency(invoice.amount),
    align: 'right',
  },
  {
    width: 2,
    header: 'Status',
    dataKey: 'balance',
    renderer: (invoice) => (invoice.balance === 0 ? 'Paid' : 'Partially Paid'),
    align: 'center',
  },
];

const PaidInvoicesPage = () => (
  <BillingInvoicePage
    fullColumnConfig={fullColumnConfig}
    mobileColumnConfig={mobileColumnConfig}
    title="Paid Invoices"
    emptyMessage={BillingConstants.EMPTY_NOTIFICATIONS.NO_PAID_INVOICES}
    showHistory={true}
    isPaymentDue={false}
  />
);

export default PaidInvoicesPage;
